import { useRef, useState } from 'react';
import { motion, useMotionValue, useTransform, type Variants } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useReducedMotionSafe } from '@/shared/MotionWrappers';
import { ChatPane } from './ChatPane';

const DRAWER_COLLAPSED_HEIGHT = 48; // px — handle bar height
const DRAG_CLOSE_THRESHOLD = 80; // px — how far user must drag down to collapse

/**
 * Fixed bottom sheet that provides the Chat on mobile viewports.
 * Collapsed: shows a labelled handle bar.
 * Expanded: full ChatPane with a collapse button at the top.
 * Supports swipe-down to collapse (skipped when reduced motion is preferred).
 *
 * z-40 — above panels (z-10 default), below dialogs (z-50).
 */
export function MobileChatDrawer() {
  const { t } = useTranslation('common');
  const reduced = useReducedMotionSafe();
  const [expanded, setExpanded] = useState(false);

  // Drag tracking for swipe-down-to-collapse
  const dragY = useMotionValue(0);
  const dragOpacity = useTransform(dragY, [0, DRAG_CLOSE_THRESHOLD], [1, 0.4]);
  const drawerRef = useRef<HTMLDivElement>(null);

  function collapse() {
    setExpanded(false);
  }

  function expand() {
    setExpanded(true);
  }

  const drawerVariants: Variants = {
    collapsed: {
      height: DRAWER_COLLAPSED_HEIGHT,
      transition: { duration: reduced ? 0 : 0.2, ease: 'easeInOut' as const },
    },
    expanded: {
      height: '70vh',
      transition: { duration: reduced ? 0 : 0.25, ease: 'easeOut' as const },
    },
  };

  return (
    <motion.div
      ref={drawerRef}
      variants={drawerVariants}
      animate={expanded ? 'expanded' : 'collapsed'}
      initial="collapsed"
      style={expanded && !reduced ? { opacity: dragOpacity } : undefined}
      className="fixed inset-x-0 bottom-0 z-40 flex flex-col overflow-hidden rounded-t-2xl border-t border-border bg-card shadow-xl"
      data-testid="mobile-chat-drawer"
    >
      {/* Handle bar / collapsed state */}
      {!expanded && (
        <button
          type="button"
          aria-label={t('mobile.openChat', { defaultValue: 'Open chat' })}
          aria-expanded={false}
          onClick={expand}
          className="relative flex h-12 w-full items-center justify-center gap-2 px-4 focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {/* Visual drag handle indicator */}
          <span className="absolute top-2 left-1/2 h-1 w-10 -translate-x-1/2 rounded-full bg-muted-foreground/40" />
          <span className="text-sm font-medium text-foreground">
            {t('mobile.openChat', { defaultValue: 'Open chat' })}
          </span>
        </button>
      )}

      {/* Expanded state */}
      {expanded && (
        <>
          {/* Collapse button row */}
          <motion.div
            drag={reduced ? false : 'y'}
            dragConstraints={{ top: 0, bottom: DRAG_CLOSE_THRESHOLD * 1.5 }}
            dragElastic={0.1}
            style={reduced ? undefined : { y: dragY }}
            onDragEnd={(_, info) => {
              if (info.offset.y > DRAG_CLOSE_THRESHOLD) {
                collapse();
              }
              dragY.set(0);
            }}
            className="flex cursor-grab items-center justify-between border-b border-border px-3 py-2 active:cursor-grabbing"
          >
            <span className="text-xs font-semibold uppercase text-muted-foreground">
              {t('mobile.chatTitle', { defaultValue: 'Chat' })}
            </span>
            <button
              type="button"
              aria-label={t('mobile.closeChat', { defaultValue: 'Close chat' })}
              aria-expanded={true}
              onClick={collapse}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <ChevronDown size={16} />
            </button>
          </motion.div>

          {/* Chat content */}
          <div className="min-h-0 flex-1 overflow-hidden">
            <ChatPane />
          </div>
        </>
      )}
    </motion.div>
  );
}
